/**
 * Seeded pseudo-random source for the view layer.
 *
 * Determinism is the point: the same seed must reproduce the same sky, the same
 * ring layout and the same drift, so a scene can be compared frame-for-frame
 * across reloads while tuning. `Math.random()` would make every measurement in
 * this phase unrepeatable.
 *
 * Mulberry32: 32 bits of state, no dependencies, good enough for placement and
 * jitter. Nothing here feeds gameplay, so statistical quality beyond "looks
 * unstructured" is not required.
 */
export type RandomSource = () => number;

export function mulberry32(seed: number): RandomSource {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform value in `[min, max)`. */
export function range(random: RandomSource, min: number, max: number): number {
  return min + random() * (max - min);
}

/** Uniform value in `[-amount, amount)`. */
export function symmetric(random: RandomSource, amount: number): number {
  return (random() * 2 - 1) * amount;
}

/** Randomly `+1` or `-1`. */
export function sign(random: RandomSource): number {
  return random() < 0.5 ? -1 : 1;
}
